import { InfoOutlined } from "@mui/icons-material";
import { Alert, Box, FormControl, FormControlLabel, IconButton, Radio, RadioGroup, Tooltip, Typography } from "@mui/material";
import { colors } from "../../../../../../../theme/muiColors";
import { ESchedulerMode } from "../../enums/event.enums";

interface Props {
  value: ESchedulerMode;
  isDisabled: boolean;
  onChange: (val: ESchedulerMode) => void;
  timeZone?: string | null;
}

const SELECTED_COLOR = colors.orange[300];
const SELECTED_BORDER_COLOR = colors.orange[400];

export const SchedulerModeSelector = ({ value, onChange, isDisabled, timeZone }: Props) => {
  return (
    <FormControl
      component="fieldset"
      sx={{
        borderRadius: 2,
        opacity: isDisabled ? 0.5 : 1,
        pointerEvents: isDisabled ? "none" : "auto",
        transition: "opacity 0.2s ease",
      }}
    >
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: "var(--fx-surface-text, #0F172A)",
            }}
          >
            Arrival time
          </Typography>
          <Tooltip
            title="Choose whether the worker must arrive at an exact time or within an arrival window."
            PopperProps={{ sx: { zIndex: 9999999 } }}
          >
            <IconButton size="small" aria-label="Arrival time help" disabled={isDisabled}>
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ my: 1, borderRadius: "8px", p: 1, bgcolor: "var(--fx-colors-info-100, #DBEAFE)" }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 300,
              color: "var(--fx-surface-text, #0F172A)",
            }}
          >
            This job will be scheduled for your <span style={{ fontWeight: 600 }}>{timeZone || "company"}</span> time zone.
          </Typography>
        </Box>
      </Box>

      <RadioGroup
        value={value}
        onChange={(e) => {
          if (!isDisabled) {
            onChange(e.target.value as ESchedulerMode);
          }
        }}
        sx={{ display: "grid", gap: 1, mt: 1 }}
      >
        <FormControlLabel
          disabled={isDisabled}
          value={ESchedulerMode.FIXED}
          control={
            <Radio
              disabled={isDisabled}
              sx={{
                "&.Mui-checked": {
                  color: SELECTED_COLOR,
                },
              }}
            />
          }
          label={
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Exact arrival time</Typography>
              <Typography sx={{ fontSize: 12, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                Worker must arrive at a specific time. Example: <b>12:30 PM</b>
              </Typography>
            </Box>
          }
          sx={{
            m: 0,
            p: 1.25,
            borderRadius: 2,
            alignItems: "flex-start",
            border: "1px solid",
            borderColor: value === ESchedulerMode.FIXED && !isDisabled ? SELECTED_BORDER_COLOR : "#d2d2d7",
            bgcolor: value === ESchedulerMode.FIXED && !isDisabled ? "rgba(255, 167, 38, 0.12)" : "transparent",
            cursor: isDisabled ? "not-allowed" : "pointer",
          }}
        />

        {/* BUFFERED */}
        <FormControlLabel
          disabled={isDisabled}
          value={ESchedulerMode.BUFFERED}
          control={
            <Radio
              disabled={isDisabled}
              sx={{
                "&.Mui-checked": {
                  color: SELECTED_COLOR,
                },
              }}
            />
          }
          label={
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Arrival window</Typography>
              <Typography sx={{ fontSize: 12, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                Worker can arrive anytime within the window. Example: <b>12:30 PM – 2:00 PM</b>
              </Typography>
            </Box>
          }
          sx={{
            m: 0,
            p: 1.25,
            borderRadius: 2,
            alignItems: "flex-start",
            border: "1px solid",
            borderColor: value === ESchedulerMode.BUFFERED && !isDisabled ? SELECTED_BORDER_COLOR : "#d2d2d7",
            bgcolor: value === ESchedulerMode.BUFFERED && !isDisabled ? "rgba(255, 167, 38, 0.12)" : "transparent",
            cursor: isDisabled ? "not-allowed" : "pointer",
          }}
        />
      </RadioGroup>

      {!isDisabled && value === ESchedulerMode.BUFFERED && (
        <Alert severity="info" sx={{ mt: 1 }}>
          The arrival window is <b>not</b> the service duration. The service can start at any time within this window.
        </Alert>
      )}
    </FormControl>
  );
};
